<script setup>
import { computed } from 'vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'

const props = defineProps({
	label: {
		type: String,
		required: true,
	},
	view: {
		type: String,
		default: 'thumbnail',
		validator: value => ['thumbnail', 'list'].includes(value),
	},
	used: {
		type: [Number, String],
		default: 0,
	},
	limit: {
		type: [Number, String],
		default: 0,
	},
	limitReached: {
		type: Boolean,
		default: false,
	},
	upgradeTo: {
		type: [String, Object],
		default: '/subscribe',
	},
})

defineEmits(['create'])

const isListView = computed(() => props.view === 'list')

// The tile stretches to its grid row (the listings use `auto-rows-fr`, so a row
// holding only the tile is as tall as the card rows); these are just floors.
const tileClasses = computed(() => [
	'w-full h-full flex items-center justify-center rounded-xl border-2 border-dashed border-surface-3 text-center bg-transparent transition-colors',
	isListView.value
		? 'min-h-[56px] flex-row gap-2 px-3 py-3'
		: 'min-h-[120px] flex-col gap-2 px-4',
])

const createClasses = computed(() => [
	...tileClasses.value,
	'text-surface-text-2 hover:text-white hover:border-surface-6',
])

const limitClasses = computed(() => [
	...tileClasses.value,
	'text-surface-text-2 hover:border-surface-6 hover:bg-surface-2 hover:no-underline',
	isListView.value ? 'gap-2' : 'gap-1.5',
])
</script>

<template>
	<router-link
		v-if="limitReached"
		:to="upgradeTo"
		:class="limitClasses"
		:aria-label="`You've reached your ${label} limit (${used}/${limit}). Upgrade for more`"
	>
		<span class="text-sm font-medium">You've reached your limit ({{ used }}/{{ limit }})</span>
		<span class="text-tiny text-main">Upgrade for more</span>
	</router-link>
	<button
		v-else
		type="button"
		:class="createClasses"
		@click="$emit('create')"
	>
		<icon-base class="text-current">
			<icon-plus />
		</icon-base>
		<span class="text-sm font-medium">New {{ label }}</span>
	</button>
</template>
